if (!customElements.get('family-pdp-selector')) {
  customElements.define(
    'family-pdp-selector',
    class FamilyPdpSelector extends HTMLElement {
      constructor() {
        super();
        this.members = [];
        this.selection = {};
        this.dimensionKeys = [];
        this.dimensionLabels = {};
        this.dimensionOrderPreferred = [];
        this.isSwitching = false;
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        const configScript = this.querySelector('[data-family-config-json]');
        const legacyScript = this.querySelector('[data-family-products-json]');

        try {
          if (configScript) {
            const cfg = JSON.parse(configScript.textContent.trim());
            this.members = Array.isArray(cfg.members) ? cfg.members : [];
            this.dimensionLabels =
              cfg.dimensionLabels && typeof cfg.dimensionLabels === 'object' && !Array.isArray(cfg.dimensionLabels)
                ? cfg.dimensionLabels
                : {};
            const order = Array.isArray(cfg.dimensionOrder) ? cfg.dimensionOrder : [];
            this.dimensionOrderPreferred = order.map((k) => String(k || '').trim()).filter(Boolean);
          } else if (legacyScript) {
            this.members = JSON.parse(legacyScript.textContent.trim());
            this.dimensionLabels = {};
            this.dimensionOrderPreferred = (this.dataset.familyDimensions || 'option1,option2,option3')
              .split(',')
              .map((k) => k.trim())
              .filter(Boolean);
          } else {
            return;
          }
        } catch (e) {
          console.warn('family-pdp: invalid config JSON', e);
          return;
        }

        if (!Array.isArray(this.members) || this.members.length < 2) {
          this.hideFamilyPdp();
          return;
        }

        this.useSkuFallback = false;
        this.normalizeMemberDimensions();

        const dimAttr =
          this.dimensionOrderPreferred.length > 0
            ? this.dimensionOrderPreferred
            : ['option1', 'option2', 'option3'];
        this.dimensionKeys = this.resolveActiveDimensions(dimAttr);

        this.ensureGlobalLoader();
        this.bindSummaryControls();

        if (this.dimensionKeys.length === 0) {
          this.hideFamilyPdp();
          return;
        }

        const currentMember = this.findMemberByUrl(this.productInfo.dataset.url) || this.members[0];
        this.dimensionKeys.forEach((key) => {
          this.selection[key] = (currentMember[key] || '').trim();
        });

        this.renderChipRows();
      }

      /**
       * Trim option values from product metafields (family-pdp-selector.liquid JSON).
       */
      normalizeMemberDimensions() {
        const dimensionFields = ['option1', 'option2', 'option3'];
        this.members = this.members.map((m) => {
          const next = { ...m };
          dimensionFields.forEach((key) => {
            next[key] = String(next[key] || '').trim();
          });
          if (!next.option1 && next.shape) next.option1 = String(next.shape).trim();
          if (!next.option2 && next.tipStyle) next.option2 = String(next.tipStyle).trim();
          return next;
        });
      }

      hideFamilyPdp() {
        this.hidden = true;
      }

      buildLabelWithInfo(displayText, ariaName) {
        const labelEl = document.createElement('span');
        labelEl.className = 'family-pdp__attr-label';

        const labelText = document.createElement('span');
        labelText.className = 'family-pdp__attr-label-text';
        labelText.textContent = displayText;

        const infoWrap = document.createElement('span');
        infoWrap.className = 'family-pdp__attr-info';
        infoWrap.setAttribute('role', 'img');
        infoWrap.setAttribute('aria-label', `${ariaName} — more information`);

        const iconUrl = this.dataset.familyInfoIconUrl;
        if (iconUrl) {
          const img = document.createElement('img');
          img.src = iconUrl;
          img.alt = '';
          img.className = 'family-pdp__attr-info-img';
          img.width = 16;
          img.height = 16;
          img.loading = 'lazy';
          img.decoding = 'async';
          infoWrap.appendChild(img);
        }

        labelEl.appendChild(labelText);
        labelEl.appendChild(infoWrap);
        return labelEl;
      }

      buildDimensionLabel(dimKey) {
        const name = this.getReadableLabel(dimKey);
        return this.buildLabelWithInfo(`${name}:`, name);
      }

      /** Approve tick (client SVG) inside a circular badge — shown when chip is selected. */
      appendChipCheckWrap(btn) {
        const wrap = document.createElement('span');
        wrap.className = 'family-pdp__chip-check';
        wrap.setAttribute('aria-hidden', 'true');
        wrap.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="family-pdp__chip-check-svg" aria-hidden="true" focusable="false"><path fill="currentColor" d="M18.7,7.2c-0.4-0.4-1-0.4-1.4,0l-7.5,7.5l-3.1-3.1c0,0,0,0,0,0c-0.4-0.4-1-0.4-1.4,0c-0.4,0.4-0.4,1,0,1.4l3.8,3.8c0.2,0.2,0.4,0.3,0.7,0.3c0.3,0,0.5-0.1,0.7-0.3l8.2-8.2C19.1,8.2,19.1,7.6,18.7,7.2z"/></svg>';
        btn.appendChild(wrap);
      }

      renderSkuFallbackChips() {
        const root = this.querySelector('[data-family-chips-root]');
        if (!root) return;
        root.innerHTML = '';

        const row = document.createElement('div');
        row.className = 'family-pdp__attr-row';
        row.setAttribute('data-family-attr-row', 'sku');

        const labelEl = this.buildLabelWithInfo('Model:', 'Model');

        const chipsWrap = document.createElement('div');
        chipsWrap.className = 'family-pdp__chips';
        chipsWrap.setAttribute('role', 'group');
        chipsWrap.setAttribute('aria-label', 'Model');

        const currentUrl = this.productInfo.dataset.url;

        this.members.forEach((m) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'family-pdp__chip';
          btn.id = `FamilyChip-sku-${this.slugId(m.sku || m.url)}`;
          const lab = document.createElement('span');
          lab.className = 'family-pdp__chip-label';
          lab.textContent = (m.sku || '').trim() || 'Option';
          btn.appendChild(lab);
          this.appendChipCheckWrap(btn);
          const isCurrent = m.url === currentUrl;
          btn.classList.toggle('is-selected', isCurrent);
          btn.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
          btn.disabled = isCurrent;
          btn.addEventListener('click', () => {
            if (btn.disabled || this.isSwitching) return;
            if (!m.url || m.url === this.productInfo.dataset.url) return;
            this.switchToProductUrl(m.url, btn.id);
          });
          chipsWrap.appendChild(btn);
        });

        row.appendChild(labelEl);
        row.appendChild(chipsWrap);
        root.appendChild(row);
        this.hideSummaryRow();
      }

      bindSummaryControls() {
        this.summaryEl = this.querySelector('[data-family-summary]');
        this.summaryValuesEl = this.querySelector('[data-family-summary-values]');
        this.clearBtn = this.querySelector('[data-family-clear-all]');
        if (this.clearBtn && !this._clearAllBound) {
          this._clearAllBound = true;
          this.clearBtn.addEventListener('click', () => this.onClearAll());
        }
      }

      hideSummaryRow() {
        if (this.summaryEl) this.summaryEl.hidden = true;
      }

      updateSelectedSummaryRow() {
        if (!this.summaryEl || !this.summaryValuesEl) return;
        if (this.useSkuFallback || !this.dimensionKeys.length) {
          this.hideSummaryRow();
          return;
        }
        const parts = this.dimensionKeys
          .map((k) => (this.selection[k] || '').trim())
          .filter(Boolean);
        if (!parts.length) {
          this.hideSummaryRow();
          return;
        }
        const wrap = this.summaryValuesEl;
        wrap.textContent = '';
        parts.forEach((part, index) => {
          if (index > 0) {
            const sep = document.createElement('span');
            sep.className = 'family-pdp__summary-sep';
            sep.setAttribute('aria-hidden', 'true');
            sep.textContent = '\u00B7';
            wrap.appendChild(sep);
          }
          const span = document.createElement('span');
          span.className = 'family-pdp__summary-part';
          span.textContent = part;
          wrap.appendChild(span);
        });
        this.summaryEl.hidden = false;
      }

      onClearAll() {
        if (this.isSwitching || this.useSkuFallback || !this.members.length) return;
        const first = this.members[0];
        this.dimensionKeys.forEach((k) => {
          this.selection[k] = String(first[k] || '').trim();
        });
        this.updateChipState();
        const match = this.findExactMember(this.selection);
        if (match && match.url && match.url !== this.productInfo.dataset.url) {
          this.switchToProductUrl(match.url, '');
        }
      }

      resolveActiveDimensions(preferredOrder) {
        const active = [];
        for (const key of preferredOrder) {
          const values = new Set(
            this.members.map((m) => (m[key] || '').trim()).filter(Boolean)
          );
          if (values.size >= 2) active.push(key);
        }
        return active;
      }

      ensureGlobalLoader() {
        if (document.querySelector('.family-pdp-loader-overlay')) return;

        const overlay = document.createElement('div');
        overlay.className = 'family-pdp-loader-overlay';
        overlay.setAttribute('aria-hidden', 'true');
        overlay.innerHTML =
          '<div class="family-pdp-loader-track" aria-hidden="true"><div class="family-pdp-loader-bar"></div></div>';
        document.body.appendChild(overlay);
      }

      renderChipRows() {
        const root = this.querySelector('[data-family-chips-root]');
        if (!root) return;
        root.innerHTML = '';

        this.dimensionKeys.forEach((dimKey) => {
          const values = this.globalValuesForDimension(dimKey);
          if (values.length < 2) return;

          const row = document.createElement('div');
          row.className = 'family-pdp__attr-row';
          row.setAttribute('data-family-attr-row', dimKey);

          const labelEl = this.buildDimensionLabel(dimKey);

          const chipsWrap = document.createElement('div');
          chipsWrap.className = 'family-pdp__chips';
          chipsWrap.setAttribute('role', 'group');
          chipsWrap.setAttribute('aria-label', this.getReadableLabel(dimKey));

          values.forEach((value) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'family-pdp__chip';
            btn.id = `FamilyChip-${this.productInfo.dataset.section}-${dimKey}-${this.slugId(value)}`;
            btn.dataset.dimension = dimKey;
            btn.dataset.value = value;
            const label = document.createElement('span');
            label.className = 'family-pdp__chip-label';
            label.textContent = value;
            btn.appendChild(label);
            this.appendChipCheckWrap(btn);
            btn.addEventListener('click', () => this.onChipClick(dimKey, value, btn));
            chipsWrap.appendChild(btn);
          });

          row.appendChild(labelEl);
          row.appendChild(chipsWrap);
          root.appendChild(row);
        });

        this.updateChipState();
        this.updateSelectedSummaryRow();
      }

      slugId(value) {
        return String(value)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }

      /** All unique values for a dimension across the whole family. */
      globalValuesForDimension(dimKey) {
        const set = new Set();
        this.members.forEach((m) => {
          const v = (m[dimKey] || '').trim();
          if (v) set.add(v);
        });
        return this.sortDimensionValues(dimKey, Array.from(set));
      }

      sortDimensionValues(dimKey, values) {
        return [...values].sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
      }

      normalizeUrl(url) {
        try {
          const parsed = new URL(url, window.location.origin);
          return `${parsed.pathname.replace(/\/+$/, '')}${parsed.search}`;
        } catch (e) {
          return String(url || '').split('?')[0].replace(/\/+$/, '');
        }
      }

      findMemberByUrl(url) {
        const target = this.normalizeUrl(url);
        return this.members.find((m) => this.normalizeUrl(m.url) === target) || null;
      }

      memberMatchesSelection(member, selection) {
        return this.dimensionKeys.every((key) => {
          const need = (selection[key] || '').trim();
          if (!need) return true;
          return (member[key] || '').trim() === need;
        });
      }

      /** True when every active dimension has a non-empty selection. */
      isSelectionComplete(selection = this.selection) {
        return this.dimensionKeys.every((key) => Boolean((selection[key] || '').trim()));
      }

      /**
       * Exact SKU for a full selection only.
       * Partial selections must not auto-resolve to a SKU (client: no auto-select).
       */
      findExactMember(selection) {
        if (!this.isSelectionComplete(selection)) return null;
        const currentUrl = this.productInfo?.dataset?.url;
        const matches = this.members.filter((m) => this.memberMatchesSelection(m, selection));
        if (!matches.length) return null;
        return matches.find((m) => this.normalizeUrl(m.url) === this.normalizeUrl(currentUrl)) || matches[0];
      }

      /**
       * Whether dim=val is compatible with the *other* currently selected values
       * (empty other dims are wildcards). Used for Available vs Unavailable styling only.
       */
      isCompatibleWithCurrentSelection(dim, val) {
        const probe = { ...this.selection, [dim]: val };
        return this.members.some((m) => this.memberMatchesSelection(m, probe));
      }

      updateChipState() {
        this.querySelectorAll('.family-pdp__chip').forEach((btn) => {
          const dim = btn.dataset.dimension;
          const val = btn.dataset.value;
          if (!dim || val == null) return;

          const isSelected = (this.selection[dim] || '') === val;
          // Mapped family values stay clickable. "Unavailable" is visual only when
          // the value does not work with the other currently selected option(s).
          const isIncompatible = !isSelected && !this.isCompatibleWithCurrentSelection(dim, val);

          btn.classList.toggle('is-selected', isSelected);
          btn.classList.toggle('is-disabled', isIncompatible);
          btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          btn.tabIndex = 0;
        });
        this.updateSelectedSummaryRow();
      }

      /**
       * Client navigation rules:
       * 1) Any mapped File Pattern / Tip Pair is always clickable.
       * 2) If new value + current other values = valid SKU → load that SKU.
       * 3) If not → keep the clicked value, clear other group(s), do not auto-pick replacements.
       * 4) Navigate only once a complete valid combination exists.
       *
       * Example: Orban+10/11 → click Buck → Buck selected, 10/11 cleared, 11/12 available.
       *          Then click 11/12 → SKU 1819.
       */
      onChipClick(dimKey, value, btn) {
        if (this.isSwitching) return;
        if ((this.selection[dimKey] || '') === value) return;

        const probe = { ...this.selection, [dimKey]: value };
        const match = this.findExactMember(probe);

        if (match) {
          this.dimensionKeys.forEach((key) => {
            this.selection[key] = (match[key] || '').trim();
          });
          this.updateChipState();

          if (!match.url || this.normalizeUrl(match.url) === this.normalizeUrl(this.productInfo.dataset.url)) {
            return;
          }
          this.switchToProductUrl(match.url, btn.id);
          return;
        }

        // Keep the option just clicked; clear incompatible other group(s). No auto-select.
        this.dimensionKeys.forEach((key) => {
          this.selection[key] = key === dimKey ? value : '';
        });
        this.updateChipState();
      }

      /** Force window to top (used after swap when layout shifts). */
      lockScrollAtTop() {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }

      smoothScrollToTop(duration = 400) {
        return new Promise((resolve) => {
          const startY = window.scrollY || window.pageYOffset || 0;
          if (startY <= 0) {
            this.lockScrollAtTop();
            resolve();
            return;
          }

          const startTime = performance.now();
          const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

          const step = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = easeOutCubic(progress);
            const nextY = Math.round(startY * (1 - eased));
            window.scrollTo(0, nextY);

            if (progress < 1 && nextY > 0) {
              requestAnimationFrame(step);
            } else {
              this.lockScrollAtTop();
              resolve();
            }
          };

          requestAnimationFrame(step);
        });
      }

      switchToProductUrl(productUrl, focusTargetId) {
        if (!productUrl || this.normalizeUrl(productUrl) === this.normalizeUrl(this.productInfo.dataset.url)) {
          return;
        }

        const minLoaderMs = 180;
        const scrollMs = 400;

        this.isSwitching = true;
        this.switchStartAt = Date.now();

        const runSwap = () => {
          const requestUrl = `${productUrl}?section_id=${this.productInfo.sectionId}`;
          const swapCallback = this.productInfo.handleSwapProduct(productUrl, false);
          const cleanup = () => {
            if (this.switchCleanupTimer) {
              window.clearTimeout(this.switchCleanupTimer);
              this.switchCleanupTimer = null;
            }
            const elapsed = Date.now() - (this.switchStartAt || Date.now());
            const remaining = Math.max(0, minLoaderMs - elapsed);

            window.setTimeout(() => {
              this.lockScrollAtTop();
              this.isSwitching = false;
              document.body.classList.remove('family-pdp-switching');
              if (typeof window.initGalleryZoom === 'function') {
                window.initGalleryZoom();
              }
              if (typeof window.initPdpProductTabs === 'function') {
                window.initPdpProductTabs('[data-pdp-product-tabs]');
              }
            }, remaining);
          };

          this.productInfo.renderProductInfo({
            requestUrl,
            targetId: focusTargetId || '',
            callback: (html) => {
              const productTitleNode = html.querySelector('.product__title h1');
              if (productTitleNode) {
                const nextTitle = productTitleNode.textContent.trim();
                const currentTitle = document.title || '';
                const separatorIndex = currentTitle.indexOf(' - ');
                const titleSuffix = separatorIndex >= 0 ? currentTitle.slice(separatorIndex) : '';
                document.title = `${nextTitle}${titleSuffix}`;
              }
              swapCallback(html);
              this.lockScrollAtTop();
              requestAnimationFrame(() => this.lockScrollAtTop());
              cleanup();
            },
          });

          this.switchCleanupTimer = window.setTimeout(cleanup, 3500);
        };

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const alreadyTop = window.scrollY <= 8;

        const startSwapWithLoader = () => {
          document.body.classList.add('family-pdp-switching');
          runSwap();
        };

        /* Scroll first — body overflow:hidden during switch blocks window scroll */
        if (alreadyTop || reduceMotion) {
          this.lockScrollAtTop();
          startSwapWithLoader();
          return;
        }

        this.smoothScrollToTop(scrollMs).then(startSwapWithLoader);
      }

      getReadableLabel(attributeKey) {
        if (
          this.dimensionLabels &&
          typeof this.dimensionLabels[attributeKey] === 'string' &&
          this.dimensionLabels[attributeKey].trim()
        ) {
          return this.dimensionLabels[attributeKey].trim();
        }
        const map = {
          option1: 'Option 1',
          option2: 'Option 2',
          option3: 'Option 3',
          shape: 'Shape',
          tipStyle: 'Tip Style',
        };
        if (map[attributeKey]) return map[attributeKey];
        const s = String(attributeKey || '')
          .replace(/([A-Z])/g, ' $1')
          .replace(/_/g, ' ')
          .trim();
        if (!s) return attributeKey;
        return s.charAt(0).toUpperCase() + s.slice(1);
      }
    }
  );
}
